import {
  Annotations,
  type ReferenceAhaObject,
  Tool,
  tools,
  type AnnotationKind,
  type ToolArgs,
  type ToolArgsWithController,
  AhaEditorUtils,
  slate,
} from '@aha-app/aha-editor-v2';
import type {
  ModelName,
  RecordReferenceStruct,
} from 'javascripts/shared-types';
import { getVisibleFieldsForItemClass } from '../ReferenceAha/utils';
import AhaDrawer from 'javascripts/drawer';
import AhaAnalytics from 'javascripts/analytics';
import { sharedRecordPickerController } from 'components/record_picker_modal';
import { type RecordPickerOptions } from 'components/record_picker_modal/controllers/RecordPickerController';
import { Annotation } from 'components/framework/models';

interface PickedRecord {
  typename: ReferenceAhaObject['class'];
  id: RecordId;
  referenceNum: string;
  path?: string;
}

function getSelectedRecordLinks(
  editor: slate.Editor,
  annotationKind: AnnotationKind
) {
  return Annotations.getAnnotations(editor).filter(
    annotation =>
      Annotations.isSelected(editor, annotation) &&
      annotation.kind === annotationKind
  );
}

async function showRecordPickerModal(
  modalOptions: Omit<Partial<RecordPickerOptions>, 'onSelect'> = {}
) {
  return new Promise<PickedRecord | undefined>(resolve => {
    sharedRecordPickerController.actionOpenSearchModal({
      actionLabel: 'Link to record',
      title: 'Select a record to link to',
      multipleRecords: false,
      createRecord:
        window.currentAccount?.roadmapFlavor ||
        window.currentAccount?.developFlavor,
      searchableTypes: [
        'Feature',
        'Idea',
        'Initiative',
        'Epic',
        'Goal',
        'Release',
        'Page',
        'Persona',
        'Competitor',
        'Task',
      ],
      hidePersonalRecords: true,
      ...modalOptions,
      onSelect(searchDocuments) {
        const records = searchDocuments
          .map(searchDocument => searchDocument.searchable)
          .filter(Boolean);

        if (!records || records.length <= 0) resolve(undefined);
        resolve(records[0] as PickedRecord);
      },
    });
  });
}

class ToolCreateRecordLinkAnnotation extends tools.ToolCreateAnnotation {
  static toolName = 'Create Aha! record';
  static iconClass = 'fa-regular aha-icon aha-icon-record-icon tw-scale-[1.2]';
  static annotationKind: AnnotationKind = 'record';

  static async action(toolArgs: ToolArgs) {
    AhaAnalytics.track(
      'Feature-7489162372883285031',
      'User clicks add record link'
    );

    let selectedText = '';

    if (toolArgs.editor?.selection) {
      selectedText = slate.Editor.string(
        toolArgs.editor,
        toolArgs.editor.selection,
        {
          voids: false,
        }
      );
    }

    const record = await showRecordPickerModal({
      defaultName: selectedText,
      parentRecordType: toolArgs.controller.props.parentType,
      parentRecordId: toolArgs.controller.props.parentId,
    });

    if (record) {
      if (selectedText) {
        super.action({
          ...toolArgs,
          annotationProperties: {
            recordType: record.typename,
            recordId: record.id,
            recordReferenceNum: record.referenceNum,
            recordPath: record.path,
          },
        });
      } else {
        const visibleFields = getVisibleFieldsForItemClass(record.typename);

        AhaEditorUtils.Marks.withRestoredMarks(toolArgs.editor, editor => {
          // Insert the record reference at the current selection
          slate.Transforms.insertNodes(editor, {
            type: 'aha-reference',
            reference_class: record.typename,
            reference_id: record.id,
            reference_name: record.referenceNum,
            reference_url: window.location.origin + record.path,
            reference_num: record.referenceNum,
            reference_visibleFields: visibleFields,
            children: [{ text: '' }],
          });

          // Move cursor after the inserted node
          AhaEditorUtils.Selection.moveAfterCurrentNode(editor);
        });

        slate.Transforms.insertText(toolArgs.editor, ' ');
      }
    }
  }

  // Only show when there is no record link annotation selected.
  // Else the open and remove record link annotation tools will be shown.
  static available(toolArgs: ToolArgsWithController) {
    if (!super.available(toolArgs)) return false;

    return !getSelectedRecordLinks(toolArgs.editor, this.annotationKind).length;
  }
}

class ToolDiscoveryCreateRecordLinkAnnotation extends ToolCreateRecordLinkAnnotation {
  static iconClass = 'aha-icon aha-icon-records tw-text-blue-500';
}
class ToolEditRecordLinkAnnotation extends Tool {
  static toolName = 'Change linked record';
  static iconClass = 'fa-regular fa-pencil';
  static annotationKind: AnnotationKind = 'record';

  static async action({ editor, controller }: ToolArgs) {
    const recordLinks = getSelectedRecordLinks(editor, this.annotationKind);
    if (recordLinks.length <= 0) return;

    const recordLink = recordLinks[0];
    if (!recordLink || recordLink.kind !== 'record') return;

    const record = await showRecordPickerModal({
      actionLabel: 'Update record link',
      title: 'Select a different record to link to',
      parentRecordType: controller.props.parentType,
      parentRecordId: controller.props.parentId,
    });

    if (record) {
      // Update the existing annotation with new record properties
      Annotations.updateAnnotation(editor, {
        key: recordLink.key,
        newProperties: {
          recordType: record.typename,
          recordId: record.id,
          recordReferenceNum: record.referenceNum,
          recordPath: record.path,
        },
      });

      // Notify controller about the updated record link
      controller.actionCreateRecordLinks({
        class: record.typename,
        id: record.id,
        name: record.referenceNum,
        url: window.location.origin + record.path,
        reference_num: record.referenceNum,
      });
    }
  }

  static available({ editor }: ToolArgsWithController) {
    return getSelectedRecordLinks(editor, this.annotationKind).length === 1;
  }
}

class ToolConvertRecordLinkToReference extends Tool {
  static toolName = 'Convert to record pill';
  static iconClass = 'fa-regular fa-swap-arrows';
  static annotationKind: AnnotationKind = 'record';

  static action({ editor, controller }: ToolArgs) {
    const recordLinks = getSelectedRecordLinks(editor, this.annotationKind);
    if (recordLinks.length <= 0) return;

    const recordAnnotation = recordLinks[0];
    if (recordAnnotation?.kind !== 'record') return;

    const visibleFields = getVisibleFieldsForItemClass(
      recordAnnotation.recordType
    );

    // Get the annotation range to replace it with the reference
    const annotationRange = recordAnnotation.range?.current;
    if (!annotationRange) return;

    AhaEditorUtils.Marks.withRestoredMarks(editor, editor => {
      // Select the annotation range
      slate.Transforms.select(editor, annotationRange);

      const annotation = new Annotation({
        id: recordAnnotation.key,
        type: 'RECORD',
      });

      void annotation.update({ configuration: { text: null } });

      // Insert the record reference at the selected range
      slate.Transforms.insertNodes(editor, {
        type: 'aha-reference',
        reference_class: recordAnnotation.recordType,
        reference_id: recordAnnotation.recordId,
        reference_name: '', // We don't have the name but it doesn't matter since the plugin loads everything it needs.
        reference_url: recordAnnotation.recordPath!,
        reference_num: recordAnnotation.recordReferenceNum,
        reference_visibleFields: visibleFields,
        children: [{ text: '' }],
      });

      // Move cursor after the inserted node
      AhaEditorUtils.Selection.moveAfterCurrentNode(editor);
    });

    slate.Transforms.insertText(editor, ' ');
  }

  static available({ editor }: ToolArgsWithController) {
    return getSelectedRecordLinks(editor, this.annotationKind).length === 1;
  }
}

class ToolOpenRecordLinkAnnotation extends Tool {
  static toolName = 'View linked record';
  static iconClass = 'aha-icon aha-icon-record-drawer';
  static annotationKind: AnnotationKind = 'record';

  static async action({ editor, controller }: ToolArgsWithController) {
    const recordLinks = getSelectedRecordLinks(editor, this.annotationKind);
    if (recordLinks.length <= 0) return;

    const recordLink = recordLinks[0];
    if (!recordLink || recordLink.kind !== 'record') return;

    return new Promise(resolve => {
      setTimeout(() => {
        const recordRef: RecordReferenceStruct = {
          typename: recordLink.recordType as ModelName,
          id: recordLink.recordId,
        };

        AhaDrawer.openRecord(recordRef);
        resolve(null);
      }, 10); // Delay to allow the annotation to be selected otherwise will block the drawer from opening.
    });
  }

  static available({ editor }: ToolArgsWithController) {
    return getSelectedRecordLinks(editor, this.annotationKind).length === 1;
  }
}

class ToolDiscoveryOpenRecordLinkAnnotation extends ToolOpenRecordLinkAnnotation {
  static iconClass = 'aha-icon aha-icon-records tw-text-blue-500';
}

class ToolbarCreateRecordLinkAnnotation extends ToolCreateRecordLinkAnnotation {
  static available(toolArgs: ToolArgsWithController) {
    // We want this to always show in the toolbar when annotations are available,
    // but only be enabled when there are no record link annotations selected,
    // handled below.
    return toolArgs.controller.ahaEditor.hasProvider('annotations');
  }

  static isEnabled(toolArgs: ToolArgsWithController): boolean {
    return !getSelectedRecordLinks(toolArgs.editor, this.annotationKind).length;
  }
}

class ToolRemoveRecordLinkAnnotation extends Tool {
  static toolName = 'Remove record link';
  static iconClass = 'fa-solid fa-trash';
  static annotationKind: AnnotationKind = 'record';

  static action({ editor, controller }: ToolArgsWithController) {
    const recordLinks = getSelectedRecordLinks(editor, this.annotationKind);
    const provider = controller.ahaEditor.getProvider('annotations');
    if (!provider) return;

    if (recordLinks.length <= 0) return;

    recordLinks.forEach(recordLink => {
      provider.onAnnotationDeleted(recordLink.key);
      Annotations.removeAnnotation(editor, { key: recordLink.key });
    });
  }

  static available({ editor, controller }: ToolArgsWithController) {
    return (
      controller.ahaEditor.hasProvider('annotations') &&
      getSelectedRecordLinks(editor, this.annotationKind).length > 0
    );
  }
}

export {
  ToolDiscoveryCreateRecordLinkAnnotation,
  ToolDiscoveryOpenRecordLinkAnnotation,
  ToolCreateRecordLinkAnnotation,
  ToolbarCreateRecordLinkAnnotation,
  ToolEditRecordLinkAnnotation,
  ToolOpenRecordLinkAnnotation,
  ToolConvertRecordLinkToReference,
  ToolRemoveRecordLinkAnnotation,
};
